import type { AppConfig } from '@/types/config'

export const seenConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1'
  },
  app: {
    id: 'seen',
    name: 'Seen',
    description: 'Stream your favorite cartoons, movies, documentaries, and educational content',
    contentId: 5,
    subscriptionPlanId: 4,
    logo: {
      default: '/assets/images/seen/logo.svg',
      icon: '/assets/images/seen/icon.svg'
    }
  },

  theme: {
    colors: {
      primary: '#FF6B35', // Vibrant orange for video streaming
      secondary: '#2C3E50', // Dark blue-gray
      accent: '#E74C3C', // Red accent
      background: '#F8F9FA' // Light gray background
    },
    dark: {
      primary: '#FF6B35', // Keep orange in dark mode
      secondary: '#BDC3C7', // Light gray
      accent: '#E74C3C', // Red accent
      background: '#1A1A1A' // Very dark background
    },
    fonts: {
      heading: 'Inter',
      body: 'Inter'
    },
    form: {
      padding: {
        container: 'py-8 px-4 sm:px-10',
        field: 'space-y-4'
      },
      rounding: {
        button: 'rounded-lg',
        input: 'rounded-lg'
      }
    }
  },

  layout: {
    maxWidth: '7xl', // Wider for video content
    headerHeight: '16',
    playerHeight: '80px', // Slightly taller for video controls
    screens: {
      home: [
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Exclusives & Originals",
            "list": [],
            "params": {
              "filter[category]": "exclusive"
            }
          },
          "title": "Exclusives & Originals",
          "limit": 20
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "New Releases",
            "list": [],
            "params": {
              "filter[category]": "new",
              "sort": "-publishedAt"
            }
          },
          "title": "New Releases"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Included in Subscription",
            "list": [],
            "params": {
              "filter[is_subscription]": 1
            }
          },
          "title": "Included in Subscription"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Documentaries",
            "list": [],
            "params": {
              "filter[tag]": "documentary"
            }
          },
          "title": "Documentaries"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        },
        {
          "type": "authors",
          "layout": "horizontal",
          "data": {
            "title": "Popular Creators",
            "list": [],
            "params": {
              "limit": 30
            }
          },
          "title": "Popular Creators"
        }
      ],
      search: [
        {
          "type": "authors",
          "layout": "two_column",
          "data": {
            "title": "Popular Creators",
            "list": [],
            "params": {
            }
          },
          "title": "Popular Creators"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        }
      ],
      library: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "recently_played",
          "data": {
            "title": "Recently Watched",
            "list": [],
            "params": {
              "filter[library]": "played"
            }
          },
          "title": "Recently Watched"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "liked",
          "data": {
            "title": "Liked",
            "list": [],
            "params": {
              "filter[library]": "liked"
            }
          },
          "title": "Liked"
        },
        {
          "type": "titles",
          "layout": "grid",
          "id": "purchased",
          "data": {
            "title": "My Library",
            "list": [],
            "params": {
              "filter[library]": "purchased"
            }
          },
          "title": "My Library"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "finished",
          "data": {
            "title": "Completed",
            "list": [],
            "params": {
              "filter[library]": "finished"
            }
          },
          "title": "Completed"
        }
      ]
    }
  },

  features: {
    shop: true,
    ads: true,
    guestMode: true,
    favorites: true,
    darkMode: true,
    search: true,
    player: true,
    sharing: true,
    epubReader: false,
    video: true, // Video feature enabled by default
    hasPreview: true,
    coverAspectRatio: 'portrait' // Movie-poster style 2:3 covers
  },

  defaultMode: 'dark' as const,

  subscription: {
    stripePlanId: 'price_1UT1jvGw3TmAggLVaTGSDhhu',
    price: '5.99',
    currency: 'GBP',
    plans: [
      {
        id: 'seen_monthly',
        interval: 'month',
        price: '5.99',
        stripePlanId: 'price_1UT1jvGw3TmAggLVaTGSDhhu',
        introPrice: '1.00',
        introDays: 14,
      },
      {
        id: 'seen_yearly',
        interval: 'year',
        price: '59.99',
        stripePlanId: 'price_1UT1jwGw3TmAggLVAKc4sH4V',
      },
    ],
    features: [
      'HD Video Streaming',
      'Offline Downloads',
      'Ad-Free Experience',
      'Multiple Devices',
      'Cancel Anytime'
    ],
    usps: [
      {
        title: 'HD Video Streaming',
        description: 'Watch all content in high-definition video for the best viewing experience.',
        icon: 'play'
      },
      {
        title: 'Offline Downloads',
        description: 'Download your favorite videos and watch them even without internet connection.',
        icon: 'download'
      },
      {
        title: 'Ad-Free Experience',
        description: 'Enjoy uninterrupted viewing with no ads or promotions.',
        icon: 'like'
      },
      {
        title: 'Exclusive Content',
        description: 'Get access to subscriber-only videos, early releases, and original productions.',
        icon: 'subscribe'
      }
    ]
  },

  urls: {
    terms: 'https://seen.plus/terms',
    privacy: 'https://seen.plus/privacy',
    support: 'https://seen.plus/support'
  },

  website: {
    siteUrl: 'https://seen.plus',
    mode: 'dark',
    menu: [
      { label: 'Home', slug: '' },
      { label: 'About', slug: 'about' },
      { label: 'Contact', slug: 'contact' },
      { label: 'Support', slug: 'support' },
    ],
    cta: {
      label: 'Join SEEN',
      href: '/subscribe/',
    },
    footer: {
      columnOne: [
        { label: 'About', slug: 'about' },
        { label: 'Subscribe', slug: 'subscribe' },
        { label: 'Contact', slug: 'contact' },
      ],
      columnTwo: [
        { label: 'Support', slug: 'support' },
        { label: 'Privacy Policy', slug: 'privacy-policy' },
        { label: 'Terms of use', slug: 'terms-of-use' },
      ],
    },
  },

  seo: {
    title: 'Seen - Your Personal Video Streaming Library',
    description: 'Stream your favorite videos, documentaries, and educational content',
    ogImage: '/assets/images/seen/og-image.svg',
    defaults: {
      titles: {
        title: 'Videos | Seen',
        description: 'Explore our collection of premium video content',
        type: 'video.other'
      },
      categories: {
        title: 'Categories | Seen',
        description: 'Browse videos by category and genre',
        type: 'website'
      },
      authors: {
        title: 'Creators | Seen',
        description: 'Discover talented video creators and their content',
        type: 'website'
      },
      series: {
        title: 'Series | Seen',
        description: 'Follow your favorite video series',
        type: 'website'
      },
      casts: {
        title: 'Hosts & Presenters | Seen',
        description: 'Meet the hosts and presenters behind our video content',
        type: 'website'
      }
    }
  }
}
